import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import JumboLayoutProvider from "@jumbo/components/JumboLayout/JumboLayoutProvider";
import JumboContentLayoutProvider from "@jumbo/components/JumboContentLayout/JumboContentLayoutProvider";
import useJumboApp from "@jumbo/hooks/useJumboApp";
import { LAYOUTS } from "./utils/constants/layouts";
import { config } from "./config/main";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CalculateOutlined from "@mui/icons-material/CalculateOutlined";
import Logout from "@mui/icons-material/Logout";
import Assessment from "@mui/icons-material/Assessment";
import ListAltIcon from "@mui/icons-material/ListAlt";
import BarChartIcon from "@mui/icons-material/BarChart";
import AddTaskIcon from "@mui/icons-material/AddTask";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import LoginIcon from "@mui/icons-material/Login";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SettingsIcon from "@mui/icons-material/Settings";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  getImpersonationOrigin,
  isValidImpersonationOrigin,
  clearImpersonationOrigin,
} from "app/utils/impersonationOrigin";
import authServices from "app/services/auth-services";
import { getCustomer, getCustomers } from "app/services/api/management";
import {
  resolveStickyHeaderActions,
  ACTION_IDS,
} from "app/navigation/stickyHeader";
import { ACCESS_SYSTEM_TICKETS_PATH } from "app/utils/constants/roleMenuConfig";

// --- MultiStore (MultiLicense) helpers ---
const safeParse = (maybeJson) => {
  if (!maybeJson) return null;
  if (typeof maybeJson === "object") return maybeJson;
  if (typeof maybeJson !== "string") return null;
  try {
    return JSON.parse(maybeJson);
  } catch (_e) {
    return null;
  }
};

const resolveRelation = (c) => {
  const parsed = safeParse(c?.dsokSettings);
  return parsed?.customer?.customerRelation ?? c?.customerRelation ?? null;
};

const getCustomerIdentifierCandidates = (c) => {
  const parsed = safeParse(c?.dsokSettings);
  const dsCustomer = parsed?.customer;
  const candidates = [
    c?._id,
    c?.id,
    c?.customerId,
    c?.url,
    dsCustomer?._id,
    dsCustomer?.id,
    dsCustomer?.customerId,
    dsCustomer?.url,
  ]
    .filter((v) => v !== undefined && v !== null)
    .map((v) => String(v).trim())
    .filter((v) => v.length > 0);
  return Array.from(new Set(candidates));
};

const getDisplayName = (c) => {
  const raw =
    c?.companyName ??
    c?.company_name ??
    c?.customerName ??
    c?.customer_name ??
    c?.name ??
    c?.title ??
    c?.storeCode ??
    c?.store_code ??
    c?.code ??
    c?.customerCode ??
    c?.customer_code ??
    "";
  const trimmed = String(raw || "").trim();
  return trimmed.length > 0 ? trimmed : getCustomerIdentifierCandidates(c)[0] ?? "";
};

const HEADER_ICON_MAP = {
  [ACTION_IDS.MULTISTORE]: StorefrontOutlinedIcon,
  [ACTION_IDS.CALCULATOR]: CalculateOutlined,
  [ACTION_IDS.CALCULATIONS]: Assessment,
  [ACTION_IDS.LOGS]: ListAltIcon,
  [ACTION_IDS.REPORTS]: BarChartIcon,
  [ACTION_IDS.DISPENSING]: AddTaskIcon,
  [ACTION_IDS.SETTINGS]: SettingsIcon,
  [ACTION_IDS.USERS]: PeopleAltOutlinedIcon,
  [ACTION_IDS.ACCESS_SYSTEM_TICKETS]: ReceiptLongIcon,
  [ACTION_IDS.LOGOUT]: Logout,
};

const HEADER_BUTTON_SX = {
  backgroundColor: "background.paper",
  borderRadius: "50%",
  boxShadow: 2,
  color: "text.primary",
  "&:hover": {
    backgroundColor: "background.paper",
    boxShadow: 3,
    transform: "scale(1.05)",
  },
  transition: "all 0.2s ease-in-out",
  width: 48,
  height: 48,
};

// Sticky Menu Component
const StickyMenu = () => {
  const { getAuthUser, setAuthToken } = useJumboAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(undefined, { useSuspense: false });
  const { enqueueSnackbar } = useSnackbar();

  // Get auth data same way as Sidebar
  const authData = getAuthUser() || {};

  const { customer, type } = authData;

  // Try to get from localStorage as fallback
  let localUser = null;
  try {
    const localData = localStorage.getItem("user");
    if (localData) {
      localUser = JSON.parse(localData);
    }
  } catch (e) {
    console.error("Error parsing localStorage:", e);
  }

  // Use localStorage data if getAuthUser doesn't return customer
  const finalCustomer = customer || localUser?.customer;
  const finalType = type || localUser?.type;
  const finalAuthUser = authData || localUser;
  const isLoggedIn = !!finalCustomer;

  const STORAGE_KEYS = React.useMemo(
    () => ({
      selectedCustomerId: "dsokSelectedCustomerId",
      selectedParentCustomerId: "dsokSelectedParentCustomerId",
    }),
    []
  );

  const handleLogout = () => {
    const origin = getImpersonationOrigin();

    const redirectAfterLogout = () => {
      if (isValidImpersonationOrigin(origin)) {
        const target = origin.originRoute.trim();
        clearImpersonationOrigin();
        window.location.href = target;
      } else {
        if (origin) clearImpersonationOrigin();
        window.location.href = "/user/login";
      }
    };

    if (finalAuthUser.mainAuth) {
      try {
        const { user, token } = finalAuthUser.mainAuth;
        const userObj = JSON.parse(user);
        const { customer, ...userRest } = userObj;
        setAuthToken({ token: token, data: { user: userRest, customer } });
        if (isValidImpersonationOrigin(origin)) {
          const target = origin.originRoute.trim();
          clearImpersonationOrigin();
          window.location.href = target;
        } else {
          if (origin) clearImpersonationOrigin();
          window.location.href = "/";
          window.location.reload();
        }
      } catch (error) {
        setAuthToken(null);
        redirectAfterLogout();
      }
      return;
    }
    setAuthToken(null);
    redirectAfterLogout();
  };

  const handleCalculatorClick = () => {
    window.location.href = "/price-calculator";
  };

  const handleCalculationsClick = () => {
    window.location.href = "/calculations";
  };

  const handleLogsClick = () => {
    window.location.href = "/logs";
  };

  const handleUsersClick = () => {
    navigate("/customer/dsok/users");
  };

  const handleSettingsClick = () => {
    const customerType =
      finalCustomer?.customerType ?? finalCustomer?.customer_type;
    const dsokType = finalCustomer?.dsokType;
    const customerUrl =
      finalCustomer?.url ??
      finalCustomer?.code ??
      safeParse(finalCustomer?.dsokSettings)?.customer?.url ??
      safeParse(finalCustomer?.dsokSettings)?.customer?.code;
    if (
      (customerType === 3 || customerType === "3") &&
      dsokType === "smartkiosk" &&
      customerUrl
    ) {
      navigate(`/dsok/customer-detail/${customerUrl}`);
    } else {
      navigate("/customer/dsok");
    }
  };

  const dsokSettings = finalCustomer?.dsokSettings;
  const dispensingOrdersUrl = React.useMemo(() => {
    const enableDispensingOrders =
      dsokSettings?.customer?.customerUISettings?.enableDispensingOrders ?? false;
    const showDispensing =
      isLoggedIn && !!finalCustomer?.dsokType && !!enableDispensingOrders;
    if (!showDispensing || !dsokSettings?.customer?.locations?.[1]) return null;
    const customer = dsokSettings.customer;
    const beDomain = customer?.beDomain ?? "";
    let env = "prod";
    if (beDomain.includes("staging")) env = "stage";
    if (typeof window !== "undefined" && window.location?.origin?.includes("localhost")) env = "dev";
    const url = `${customer.code}/${customer.locations[1].code}/${env}/${customer.customerId}`;
    return `/customer/dsok/dispensing-orders/${url}`;
  }, [isLoggedIn, finalCustomer?.dsokType, dsokSettings]);

  const handleReportsClick = () => {
    navigate("/customer/dsok/reports-overview");
  };

  const handleDispensingOrdersClick = () => {
    if (dispensingOrdersUrl) window.open(dispensingOrdersUrl, "_blank");
  };

  // --- MultiStore (MultiLicense) logic (preserves existing behavior) ---
  const currentRelation = resolveRelation(finalCustomer) || {};
  const relationshipType = currentRelation?.relationshipType ?? null;
  const customerTypeRelation = currentRelation?.customerType ?? null;
  const parentCustomerIdRel = currentRelation?.parentCustomerId ?? null;
  // A child inherits relationshipType from its parent, but that inherited value
  // may not be persisted on the child. Treat a child that references a parent as
  // eligible; loadGroup confirms the parent is really multiLicense.
  const isMultiLicense =
    isLoggedIn &&
    (relationshipType === "multiLicense" ||
      (customerTypeRelation === "child" && !!parentCustomerIdRel));

  const [mlParentId, setMlParentId] = React.useState("");
  const [mlOptions, setMlOptions] = React.useState([]); // [{ id, label, isParent, customer }]
  const [mlLoadError, setMlLoadError] = React.useState(false);
  const [isLoadingMl, setIsLoadingMl] = React.useState(false);
  const [isSwitchingId, setIsSwitchingId] = React.useState(null);
  const [currentCompanyId, setCurrentCompanyId] = React.useState("");
  const [isMultiStoreOpen, setIsMultiStoreOpen] = React.useState(false);
  const loadedKeyRef = React.useRef("");

  const customerBootstrapKey = React.useMemo(() => {
    const ids = getCustomerIdentifierCandidates(finalCustomer);
    const self = ids[0] ?? "";
    const parent = currentRelation?.parentCustomerId ?? "";
    return `${String(self)}:${String(relationshipType)}:${String(parent)}`;
  }, [finalCustomer, relationshipType, currentRelation?.parentCustomerId]);

  // Clear selection keys when relationshipType is not multiLicense.
  React.useEffect(() => {
    if (isMultiLicense) return;
    try {
      localStorage.removeItem(STORAGE_KEYS.selectedCustomerId);
      localStorage.removeItem(STORAGE_KEYS.selectedParentCustomerId);
    } catch (_e) {}
    setMlParentId("");
    setMlOptions([]);
    setMlLoadError(false);
    setCurrentCompanyId("");
    loadedKeyRef.current = "";
  }, [
    isMultiLicense,
    STORAGE_KEYS.selectedCustomerId,
    STORAGE_KEYS.selectedParentCustomerId,
  ]);

  React.useEffect(() => {
    const loadGroup = async () => {
      if (!isMultiLicense) return;
      if (loadedKeyRef.current === customerBootstrapKey) return;
      loadedKeyRef.current = customerBootstrapKey;

      setIsLoadingMl(true);
      setMlLoadError(false);
      try {
        const result = await getCustomers(1);
        const list = Array.isArray(result) ? result : [];
        const customersData = list.map((item) => ({
          ...(item?.customerData || {}),
          users: item?.users,
        }));

        // Resolve parentId according to source-of-truth rules, but for "parent"
        // pick the identifier children actually reference.
        let resolvedParentId = "";
        if (customerTypeRelation === "child") {
          resolvedParentId = String(currentRelation?.parentCustomerId ?? "");
        } else if (customerTypeRelation === "parent") {
          const authIds = getCustomerIdentifierCandidates(finalCustomer);
          const selfMatch =
            customersData.find((c) => {
              const ids = getCustomerIdentifierCandidates(c);
              return ids.some((id) => authIds.includes(String(id)));
            }) || null;
          const parentCandidates = selfMatch
            ? getCustomerIdentifierCandidates(selfMatch)
            : authIds;

          // Pick the identifier children actually reference. Match on
          // parentCustomerId only — a child's own relationshipType is inherited
          // from the parent and may be missing/null, so it must not gate which
          // identifier the group hangs on.
          let best = parentCandidates[0] ?? "";
          let bestCount = -1;
          for (const cand of parentCandidates) {
            const count = customersData.filter((c) => {
              const r = resolveRelation(c);
              return (
                r?.customerType === "child" &&
                String(r?.parentCustomerId ?? "") === String(cand)
              );
            }).length;
            if (count > bestCount) {
              bestCount = count;
              best = cand;
            }
          }
          resolvedParentId = best;
        }

        if (!resolvedParentId) {
          setMlParentId("");
          setMlOptions([]);
          return;
        }

        // The parent is the single source of truth for the relationship type.
        // Only build a switch group when the parent is actually multiLicense;
        // children inherit this and are grouped by parentCustomerId regardless
        // of whether their own (inherited) relationshipType was persisted.
        const parentCustomer = customersData.find((c) =>
          getCustomerIdentifierCandidates(c).includes(String(resolvedParentId))
        );
        const parentRelationshipType =
          resolveRelation(parentCustomer)?.relationshipType ?? null;
        if (parentRelationshipType !== "multiLicense") {
          setMlParentId("");
          setMlOptions([]);
          return;
        }

        const group = customersData.filter((c) => {
          const r = resolveRelation(c);
          const ids = getCustomerIdentifierCandidates(c);
          if (ids.includes(String(resolvedParentId))) return true; // parent
          if (r?.customerType !== "child") return false;
          return String(r?.parentCustomerId ?? "") === String(resolvedParentId);
        });

        const parent = group.find((c) =>
          getCustomerIdentifierCandidates(c).includes(String(resolvedParentId))
        );
        const children = group
          .filter(
            (c) =>
              !getCustomerIdentifierCandidates(c).includes(String(resolvedParentId))
          )
          .sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b)));

        const sorted = [parent, ...children].filter(Boolean);
        const options = sorted.map((c) => {
          const ids = getCustomerIdentifierCandidates(c);
          const isParent = ids.includes(String(resolvedParentId));
          const id = isParent ? String(resolvedParentId) : ids[0] ?? "";
          const name = getDisplayName(c);
          return { id, name, isParent, customer: c };
        });

        setMlParentId(String(resolvedParentId));
        setMlOptions(options);

        // Determine current active company id for UI marking
        const authIds = getCustomerIdentifierCandidates(finalCustomer);
        const currentOption = options.find((o) => {
          const ids = getCustomerIdentifierCandidates(o.customer);
          return ids.some((id) => authIds.includes(String(id)));
        });
        const activeId = String(currentOption?.id ?? "");
        setCurrentCompanyId(activeId);

        // Persist a stable selection if present and valid; otherwise default per rules.
        const allowedIds = new Set(options.map((o) => String(o.id)));
        const persistedParentId = localStorage.getItem(
          STORAGE_KEYS.selectedParentCustomerId
        );
        const persistedSelectedId = localStorage.getItem(
          STORAGE_KEYS.selectedCustomerId
        );

        let defaultId = String(resolvedParentId);
        if (customerTypeRelation === "child") {
          defaultId = activeId || defaultId;
        }

        const initialSelected =
          persistedParentId &&
          String(persistedParentId) === String(resolvedParentId) &&
          persistedSelectedId &&
          allowedIds.has(String(persistedSelectedId))
            ? String(persistedSelectedId)
            : String(defaultId);

        localStorage.setItem(
          STORAGE_KEYS.selectedParentCustomerId,
          String(resolvedParentId)
        );
        localStorage.setItem(STORAGE_KEYS.selectedCustomerId, initialSelected);
      } catch (_e) {
        setMlLoadError(true);
        setMlOptions([]);
        enqueueSnackbar(
          t("multistore.loadError", { defaultValue: "Failed to load companies." }),
          { variant: "warning" }
        );
      } finally {
        setIsLoadingMl(false);
      }
    };

    loadGroup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerBootstrapKey]);

  const handleLoginAs = async (option) => {
    const nextId = String(option?.id ?? "");
    if (!nextId) return;
    if (isSwitchingId) return;

    const allowedIds = new Set(mlOptions.map((o) => String(o.id)));
    if (!allowedIds.has(nextId)) {
      enqueueSnackbar(
        t("multistore.loginError", { defaultValue: "Failed to switch company." }),
        { variant: "error" }
      );
      return;
    }

    const targetCustomer = option?.customer;
    const user = Array.isArray(targetCustomer?.users) ? targetCustomer.users[0] : null;
    if (!user?.username || !user?.password) {
      enqueueSnackbar(
        t("multistore.loginError", { defaultValue: "Failed to switch company." }),
        { variant: "error" }
      );
      return;
    }

    setIsSwitchingId(nextId);
    try {
      localStorage.setItem(STORAGE_KEYS.selectedParentCustomerId, String(mlParentId));
      localStorage.setItem(STORAGE_KEYS.selectedCustomerId, nextId);

      const data = await authServices.signIn({
        username: user.username,
        password: user.password,
        type: "hash",
      });

      setAuthToken(data, true);
      if (data?.data?.customer?.url) {
        await getCustomer(data.data.customer.url);
      }
      enqueueSnackbar(
        `${t("buttons.loginAsCustomer", {
          defaultValue: "Login as customer",
        })} ${t("notifications.wasSuccessful", {
          defaultValue: "was successful",
        })}`,
        { variant: "success" }
      );

      setIsMultiStoreOpen(false);
      navigate("/");
    } catch (_e) {
      enqueueSnackbar(
        t("multistore.loginError", { defaultValue: "Failed to switch company." }),
        { variant: "error" }
      );
    } finally {
      setIsSwitchingId(null);
    }
  };

  const headerActions = React.useMemo(
    () =>
      resolveStickyHeaderActions({
        finalCustomer,
        finalType,
        isLoggedIn,
      }),
    [finalCustomer, finalType, isLoggedIn]
  );

  const headerHandlers = {
    [ACTION_IDS.MULTISTORE]: () => setIsMultiStoreOpen(true),
    [ACTION_IDS.CALCULATOR]: handleCalculatorClick,
    [ACTION_IDS.CALCULATIONS]: handleCalculationsClick,
    [ACTION_IDS.LOGS]: handleLogsClick,
    [ACTION_IDS.REPORTS]: handleReportsClick,
    [ACTION_IDS.DISPENSING]: handleDispensingOrdersClick,
    [ACTION_IDS.SETTINGS]: handleSettingsClick,
    [ACTION_IDS.USERS]: handleUsersClick,
    [ACTION_IDS.ACCESS_SYSTEM_TICKETS]: () => navigate(ACCESS_SYSTEM_TICKETS_PATH),
    [ACTION_IDS.LOGOUT]: handleLogout,
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1100,
        backgroundColor: "background.default",
        px: 3,
        width: "100%",
        maxHeight: "100px",
        minHeight: "60px",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {headerActions
          .filter((action) => action.visible)
          .map((action) => {
            const IconComponent = HEADER_ICON_MAP[action.iconId];
            const title = action.tooltipKey
              ? t(action.tooltipKey, { defaultValue: action.tooltipDefault })
              : action.tooltip;
            const ariaLabel = action.ariaLabelKey
              ? t(action.ariaLabelKey, {
                  defaultValue: action.ariaLabelDefault ?? action.ariaLabel,
                })
              : action.ariaLabel;
            const onClick = headerHandlers[action.id];
            return (
              <Tooltip key={action.id} title={title}>
                <IconButton
                  onClick={onClick}
                  sx={HEADER_BUTTON_SX}
                  size="small"
                  aria-label={ariaLabel}
                >
                  {IconComponent && (
                    <IconComponent fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            );
          })}
      </Box>

      <Dialog
        open={isMultiStoreOpen}
        onClose={() => setIsMultiStoreOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t("multistore.modalTitle", { defaultValue: "Switch company" })}
        </DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={1}>
            {isLoadingMl && (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={18} />
                <Typography variant="body2">
                  {t("multistore.loading", { defaultValue: "Loading..." })}
                </Typography>
              </Box>
            )}

            {!isLoadingMl && mlLoadError && (
              <Typography variant="body2" color="error">
                {t("multistore.loadError", { defaultValue: "Failed to load companies." })}
              </Typography>
            )}

            {!isLoadingMl && !mlLoadError && mlOptions.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                {t("multistore.empty", { defaultValue: "No companies found." })}
              </Typography>
            )}

            {!isLoadingMl && mlOptions.length > 0 && (
              <Stack spacing={1} divider={<Divider flexItem />}>
                {mlOptions.map((o) => {
                  const isCurrent = String(o.id) === String(currentCompanyId);
                  return (
                    <Box
                      key={String(o.id)}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={2}
                      sx={{ py: 0.5 }}
                    >
                      <Box minWidth={0}>
                        <Box display="flex" alignItems="center" gap={1} minWidth={0}>
                          {isCurrent && (
                            <Tooltip
                              title={t("multistore.currentLabel", {
                                defaultValue: "Current",
                              })}
                            >
                              <CheckCircleIcon
                                fontSize="small"
                                sx={{ color: "success.main", flex: "0 0 auto" }}
                              />
                            </Tooltip>
                          )}
                          <Typography variant="body2" noWrap title={o.name}>
                            {o.name}
                          </Typography>
                          {o.isParent && (
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {t("multistore.parentBadge", { defaultValue: "Parent" })}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Tooltip title={t("multistore.loginTooltip", { defaultValue: "Log in" })}>
                        <span>
                          <IconButton
                            size="small"
                            aria-label={t("multistore.loginTooltip", { defaultValue: "Log in" })}
                            disabled={Boolean(isSwitchingId)}
                            onClick={() => handleLoginAs(o)}
                          >
                            {isSwitchingId === String(o.id) ? (
                              <CircularProgress size={18} />
                            ) : (
                              <LoginIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsMultiStoreOpen(false)}>
            {t("multistore.close", { defaultValue: "Close" })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const AppLayout = (props) => {
  const { activeLayout } = useJumboApp();
  const { isLoading, getAuthUser } = useJumboAuth();
  const location = useLocation();

  // Check if user is logged in
  const authData = getAuthUser() || {};
  const { customer } = authData;
  let localUser = null;
  try {
    const localData = localStorage.getItem("user");
    if (localData) {
      localUser = JSON.parse(localData);
    }
  } catch (e) {
    console.error("Error parsing localStorage:", e);
  }
  const finalCustomer = customer || localUser?.customer;
  const isLoggedIn = !!finalCustomer;

  // Do not show StickyMenu on "full screen" pages
  const hideStickyMenu = React.useMemo(() => {
    const p = location?.pathname || "";
    return (
      p.startsWith("/customer/dsok/dispensing-orders") ||
      p.startsWith("/order-status-screen") ||
      p.startsWith("/daily-menu-screen") ||
      p.startsWith("/dsok/processed-order")
    );
  }, [location?.pathname]);
  const showStickyMenu = isLoggedIn && !hideStickyMenu;

  if (!activeLayout) {
    throw Error("AppLayout > No activeLayout is set.");
  }

  const LayoutComponent = React.useMemo(() => {
    const layoutIndex = LAYOUTS.findIndex(
      (layout) => layout.name === activeLayout,
    );

    if (layoutIndex >= 0) {
      return LAYOUTS[layoutIndex].component;
    }

    throw Error("No activeLayout is set yet.");
  }, [activeLayout]);

  return (
    <JumboLayoutProvider>
      {isLoading ? (
        <div className="loader">
          <svg className="circular-loader" viewBox="25 25 50 50">
            <circle className="loader-path" cx="50" cy="50" r="20" />
          </svg>
        </div>
      ) : (
        <LayoutComponent>
          {showStickyMenu && <StickyMenu />}
          <Box sx={{ paddingTop: showStickyMenu ? "40px" : "0px" }}>
            <JumboContentLayoutProvider
              layoutOptions={config.defaultContentLayout}
            >
              {props.children}
            </JumboContentLayoutProvider>
          </Box>
        </LayoutComponent>
      )}
    </JumboLayoutProvider>
  );
};

export default AppLayout;
